var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import memoizeOne from 'memoize-one';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { components } from 'react-select';
import StylePropType from 'react-style-proptype';
import Select from '../Select';
import CURRENCY_CODES from './currencyCodes';
const emojiContainerStyle = { display: 'flex', alignItems: 'center' };
const emojiStyle = { display: 'inline-block', width: '18px' };
const CustomCurrencySelect = ({ value, flag, label, displayFullName }) => (React.createElement("div", { style: emojiContainerStyle },
    React.createElement("span", { style: emojiStyle, dangerouslySetInnerHTML: { __html: flag } }),
    "\u00A0 ",
    displayFullName ? `${label} (${value})` : value));
CustomCurrencySelect.propTypes = {
    value: PropTypes.string,
    label: PropTypes.string,
    displayFullName: PropTypes.bool,
    flag: PropTypes.node,
};
class CurrencySelector extends PureComponent {
    constructor() {
        super(...arguments);
        this.onChangeCurrency = currency => {
            const { onChange } = this.props;
            onChange(currency);
        };
        this.getCurrencyCodes = memoizeOne(disabledCurrencies => {
            // This prevents every currency selector from making a copy of the list of currencies.
            // Because the common use case is to not disable currencies, most selectors will not copy the list.
            if (!disabledCurrencies || disabledCurrencies.length === 0) {
                return CURRENCY_CODES;
            }
            const disabledCurrencySet = new Set(disabledCurrencies);
            // eslint-disable-next-line no-confusing-arrow
            const currencies = CURRENCY_CODES.map(currency => disabledCurrencySet.has(currency.value) ? Object.assign(Object.assign({}, currency), { disabled: true }) : currency);
            return currencies;
        });
        this.getCurrencyValue = codeOptions => {
            const { currency } = this.props;
            if (Array.isArray(currency)) {
                return currency.map(selectedCurrency => {
                    const currencyIdx = codeOptions.findIndex(option => option.value === selectedCurrency);
                    return codeOptions[currencyIdx];
                });
            }
            const currencyIdx = codeOptions.findIndex(option => option.value === currency);
            return codeOptions[currencyIdx];
        };
    }
    render() {
        const _a = this.props, { className, clearable, currency, disabled, disabledCurrencies, displayFullName, label, required, style } = _a, rest = __rest(_a, ["className", "clearable", "currency", "disabled", "disabledCurrencies", "displayFullName", "label", "required", "style"]);
        const currencyClassName = `currency-select ${className}`;
        const currencyCodes = this.getCurrencyCodes(disabledCurrencies);
        return (React.createElement(Select, Object.assign({ required: required, label: label, disabled: disabled, className: currencyClassName, 
            // This is the v2+ way of handling custom styling for various parts of the select
            // The inline functions are done in order to pass displayFullName along for the display logic
            components: {
                Option: (_a) => {
                    var { data } = _a, props = __rest(_a, ["data"]);
                    return (React.createElement(components.Option, Object.assign({}, props),
                        React.createElement(CustomCurrencySelect, Object.assign({}, data, { displayFullName: displayFullName }))));
                },
                SingleValue: (_a) => {
                    var { data } = _a, props = __rest(_a, ["data"]);
                    return (React.createElement(components.SingleValue, Object.assign({}, props),
                        React.createElement(CustomCurrencySelect, Object.assign({}, data, { displayFullName: displayFullName }))));
                },
                MultiValue: (_a) => {
                    var { data } = _a, props = __rest(_a, ["data"]);
                    return (React.createElement(components.MultiValue, Object.assign({}, props),
                        React.createElement(CustomCurrencySelect, Object.assign({}, data, { displayFullName: displayFullName }))));
                },
            }, isOptionDisabled: option => option.disabled, clearable: clearable, onChange: this.onChangeCurrency, options: currencyCodes, value: this.getCurrencyValue(currencyCodes), style: style }, rest)));
    }
}
CurrencySelector.propTypes = {
    onChange: PropTypes.func.isRequired,
    className: PropTypes.string,
    currency: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    disabled: PropTypes.bool,
    disabledCurrencies: PropTypes.arrayOf(PropTypes.string),
    isMulti: PropTypes.bool,
    label: PropTypes.string,
    required: PropTypes.bool,
    displayFullName: PropTypes.bool,
    clearable: PropTypes.bool,
    style: StylePropType,
};
CurrencySelector.defaultProps = {
    disabled: false,
    disabledCurrencies: [],
    isMulti: false,
    className: '',
    required: false,
    displayFullName: false,
    clearable: false,
    currency: null,
    label: 'Currency',
    style: {},
};
export default CurrencySelector;
